import { Controller } from "@hotwired/stimulus"

// Priority-plus toolbar overflow.
//
// The collapsible tool buttons live in the [bar] target — a flex row with
// `overflow: hidden`. Whenever they don't all fit, the ones that overflow are
// moved (right-most / lowest-priority first) into the [menu] dropdown, which is
// revealed by the [button] (⋮). Everything is restored to the bar and
// re-measured on every resize via a ResizeObserver, so the toolbar adapts to
// ANY width with no hard-coded breakpoints. The ⋮ group and anything after the
// bar (the account menu) live OUTSIDE the clipped bar, so they are never hidden
// and the dropdown is never clipped.
export default class extends Controller {
  static targets = ["bar", "more", "button", "menu"]

  connect() {
    // Snapshot the collapsible items in their original (priority) order so we
    // can always rebuild the bar in the same sequence.
    this._items = Array.from(this.barTarget.children)
    this._onDocClick = (e) => { if (!this.moreTarget.contains(e.target)) this.close() }
    this._onKey = (e) => { if (e.key === "Escape") this.close() }
    document.addEventListener("click", this._onDocClick)
    document.addEventListener("keydown", this._onKey)
    this._ro = new ResizeObserver(() => this.reflow())
    this._ro.observe(this.barTarget)
    // Icon fonts / SVGs can settle after connect and change widths — reflow
    // once more on the next frame so the first measurement isn't stale.
    this._raf = requestAnimationFrame(() => this.reflow())
  }

  disconnect() {
    this._ro?.disconnect()
    cancelAnimationFrame(this._raf)
    document.removeEventListener("click", this._onDocClick)
    document.removeEventListener("keydown", this._onKey)
  }

  toggle(event) {
    event.stopPropagation()
    this.moreTarget.classList.toggle("is-open")
    this.buttonTarget.setAttribute("aria-expanded", String(this.moreTarget.classList.contains("is-open")))
  }

  close() {
    if (!this.moreTarget.classList.contains("is-open")) return
    this.moreTarget.classList.remove("is-open")
    this.buttonTarget.setAttribute("aria-expanded", "false")
  }

  reflow() {
    // 1) restore every collapsible item to the bar (original order), strip any
    //    menu labels, empty the menu, and hide the ⋮ group.
    this._items.forEach((el) => { this._decorate(el, false); this.barTarget.appendChild(el) })
    this.menuTarget.replaceChildren()
    this.moreTarget.hidden = true

    // 2) nothing overflows → all tools stay inline, ⋮ stays hidden.
    if (!this._overflowing()) return

    // 3) reveal ⋮ (it reserves room too) then move items — right-most first —
    //    into the menu until the row fits or nothing collapsible is left.
    this.moreTarget.hidden = false
    for (let i = this._items.length - 1; i >= 0 && this._overflowing(); i--) {
      this._decorate(this._items[i], true)
      this.menuTarget.insertBefore(this._items[i], this.menuTarget.firstChild)
    }
    this._trimDividers()
  }

  _overflowing() {
    // scrollWidth reports the content extent even with overflow:visible, so
    // this stays true even though the bar isn't clipped (which would break the
    // inline language-picker dropdown). +1 guards sub-pixel rounding.
    return this.barTarget.scrollWidth > this.barTarget.clientWidth + 1
  }

  // Icon-only tool buttons carry their name only in `title`. When one lands in
  // the vertical menu, append a text label so the row is readable; strip it
  // again when the button returns to the icon-only bar.
  _decorate(el, inMenu) {
    if (!el || !el.querySelectorAll) return
    const btns = el.classList.contains("en-tb-btn") ? [el] : Array.from(el.querySelectorAll(".en-tb-btn"))
    btns.forEach((b) => {
      let lbl = b.querySelector(":scope > .en-more-lbl")
      if (inMenu) {
        if (!lbl) {
          lbl = document.createElement("span")
          lbl.className = "en-more-lbl"
          lbl.textContent = b.getAttribute("title") || b.getAttribute("aria-label") || ""
          b.appendChild(lbl)
        }
      } else if (lbl) {
        lbl.remove()
      }
    })
  }

  // Drop dividers that ended up leading/trailing in the menu, or trailing in
  // the bar, so a group separator never dangles with nothing beside it.
  _trimDividers() {
    const isDiv = (el) => el && el.classList && el.classList.contains("en-tb-divider")
    while (isDiv(this.menuTarget.firstElementChild)) this.menuTarget.firstElementChild.remove()
    while (isDiv(this.menuTarget.lastElementChild)) this.menuTarget.lastElementChild.remove()
    while (isDiv(this.barTarget.lastElementChild)) this.barTarget.lastElementChild.remove()
  }
}
